import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import supabase from '@/lib/supabaseClient';
import { getActiveRegionConfig, getPortalHeaders } from '@/lib/regionConfig';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let handled = false;
    let mounted = true;
    // Reset-password links come back with type=recovery (query or hash); they must never run the signup path.
    const isRecoveryLink = new URLSearchParams(window.location.search).get('type') === 'recovery'
      || new URLSearchParams(window.location.hash.replace(/^#/, '')).get('type') === 'recovery';

    const notifyPostVerify = async (session) => {
      const accessToken = session?.access_token;
      if (!accessToken) return false;

      try {
        const regionConfig = getActiveRegionConfig();
        const resp = await fetch(`${API_URL}/api/auth/post-verify`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, ...getPortalHeaders(regionConfig) },
        });
        if (!resp.ok) {
          console.error('post-verify returned', resp.status);
          return false;
        }
        return true;
      } catch (err) {
        console.error('post-verify call failed:', err);
        return false;
      }
    };

    const completeSignupCallback = async (session) => {
      if (handled || !mounted) return;
      handled = true;
      if (isRecoveryLink) {
        navigate('/reset-password', { replace: true });
        return;
      }
      await notifyPostVerify(session);
      navigate('/pending-approval', { replace: true });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        const userPortal = session.user?.app_metadata?.portal;
        if (userPortal === 'provider') {
          const isProduction = !window.location.hostname.startsWith('preview.') && window.location.hostname !== 'localhost';
          window.location.href = isProduction
            ? 'https://pp-fe-l.digitalreporting.uk/reset-password'
            : 'https://pp-fe-d.digitalreporting.uk/reset-password';
        } else {
          navigate('/reset-password', { replace: true });
        }
        return;
      }

      if (event === 'SIGNED_IN' && session) {
        await completeSignupCallback(session);
      }
    });

    const finishFromCurrentUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has('code')) {
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(window.location.href);
        if (exchangeError) {
          console.error('exchangeCodeForSession failed:', exchangeError);
        }
        if (data?.session) {
          await completeSignupCallback(data.session);
          return;
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await completeSignupCallback(session);
      }
    };

    finishFromCurrentUrl();

    const timeout = setTimeout(() => {
      if (!handled && mounted) {
        setError('This confirmation link has expired or was already used. Please request a new one.');
      }
    }, 10000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm text-center">
          <div className="bg-card border border-border rounded-lg p-8">
            <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Verification failed</h2>
            <p className="text-sm text-muted-foreground mb-6">{error}</p>
            <div className="space-y-3">
              <Link to="/signup" className="block w-full text-center bg-primary text-primary-foreground py-2 px-4 rounded-md text-sm font-medium hover:bg-primary/90">
                Register again
              </Link>
              <Link to="/login" className="block text-sm font-medium text-primary hover:underline">
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-muted-foreground">Confirming your account...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
